import {
  Button,
  FileInput,
  Group,
  Modal,
  Portal,
  Progress,
  rem,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconFile, IconFolder } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { client } from "@/api-client";
import type { FileOutput } from "@/api-types";
import { getContentType } from "@/utils/content-types";
import { PreviewElement } from "../pages/universal/FilePreview";

interface FileUploadModalProps {
  onSuccess: (file: FileOutput) => void;
  accept?: string;
  folderId?: string;
}

export const useFileUploadModal = ({
  onSuccess,
  accept,
  folderId,
}: FileUploadModalProps) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [folderOptions, setFolderOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const fileUploadMutation = client.useMutation(
    "post",
    "/universal/file-upload",
  );
  const addToLibraryMutation = client.useMutation("post", "/universal/files/");

  const { data: filesData } = client.useQuery(
    "get",
    "/universal/files/",
    {},
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  const form = useForm({
    name: "file-upload-form",
    mode: "uncontrolled",
    initialValues: {
      fileName: "",
      description: "",
      parentFolderId: folderId ?? "",
    },
    enhanceGetInputProps: () => ({ disabled: !file }),
    validate: {
      fileName: isNotEmpty("File name is required"),
    },
  });

  // Process folder data when it loads
  useEffect(() => {
    if (filesData?.folders) {
      const options = [{ value: "", label: "Root" }];
      filesData.folders.forEach((folder) => {
        options.push({
          value: folder.id,
          label: folder.name,
        });
      });
      if (folderId) {
        setFolderOptions(options.filter((option) => option.value === folderId));
      } else {
        setFolderOptions(options);
      }
    }
  }, [filesData, folderId]);

  const handleFileSelect = (selectedFile: File | null) => {
    setFile(selectedFile);
    if (!selectedFile) {
      setFileUrl(null);
      return;
    }
    form.reset();
    if (folderId) {
      form.setFieldValue("parentFolderId", folderId);
    }
    const fileNameWithoutExtension = selectedFile.name.replace(/\.[^/.]+$/, "");
    form.setValues({
      fileName: fileNameWithoutExtension,
    });
    const url = URL.createObjectURL(selectedFile);
    setFileUrl(url);
  };

  const handleUpload = async (values: typeof form.values) => {
    if (!file) return;

    try {
      setUploading(true);

      // Get upload URL and params
      const fileUpload = await fileUploadMutation.mutateAsync({});
      const formData = new FormData();

      Object.entries(fileUpload.params.fields).forEach(([k, v]) =>
        formData.append(k, v),
      );
      formData.append("file", file);

      // Upload file
      await fetch(fileUpload.params.url, {
        method: "POST",
        body: formData,
      });
      // Add to library
      const result = await addToLibraryMutation.mutateAsync({
        body: {
          uploadId: fileUpload.id,
          originalName: file.name,
          name: values.fileName,
          description: values.description,
          type: getContentType(file.type),
          parentFolderId: values.parentFolderId || null,
          isPublic: false,
        },
      });

      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
      setFile(null);
      setFileUrl(null);
      setUploading(false);
      if (result) {
        onSuccess(result);
      }
      close();
    } catch (error) {
      console.error("Upload failed:", error);
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const modal = opened ? (
    <Portal>
      <Modal
        opened={opened}
        onClose={close}
        title={"Upload File"}
        centered
        size="lg"
      >
        <form name="file-upload-form" onSubmit={form.onSubmit(handleUpload)}>
          <Stack>
            <FileInput
              onChange={handleFileSelect}
              leftSection={
                <IconFile
                  style={{
                    width: rem(18),
                    height: rem(18),
                  }}
                  stroke={1.5}
                />
              }
              label="Upload a file"
              placeholder="Click here to pick a file"
              leftSectionPointerEvents="none"
              accept={accept}
              disabled={uploading}
            />

            {file && (
              <Text>This looks like a {getContentType(file.type)} file.</Text>
            )}

            <TextInput
              withAsterisk
              label="File name"
              key={form.key("fileName")}
              {...form.getInputProps("fileName")}
            />

            <Textarea
              label="Description"
              key={form.key("description")}
              {...form.getInputProps("description")}
            />

            <Select
              disabled={!!folderId}
              label="Folder"
              placeholder="Select a folder"
              data={folderOptions}
              key={form.key("parentFolderId")}
              leftSection={<IconFolder size={16} />}
              {...form.getInputProps("parentFolderId")}
            />

            {file && fileUrl && (
              <>
                <Text fw={500}>Preview</Text>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <PreviewElement
                    file={{
                      previewUrl: fileUrl,
                      type: getContentType(file.type),
                      mimeType: file?.type,
                    }}
                  />
                </div>
              </>
            )}

            {uploading && <Progress value={0} size="sm" />}

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={close} disabled={uploading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!file || uploading}
                loading={uploading}
              >
                Upload
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Portal>
  ) : null;

  return {
    modal,
    open,
  };
};

export const FileUploadModal = ({
  onSuccess,
  accept,
}: FileUploadModalProps) => {
  const { modal, open } = useFileUploadModal({ onSuccess, accept });
  return (
    <>
      <Button
        variant="outline"
        leftSection={<IconFile size="1.2em" />}
        onClick={open}
        style={{ flex: 1 }}
        className="FileUploadModal-trigger"
      >
        Upload New File
      </Button>
      {modal}
    </>
  );
};
